import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

/** iter474 42 — traitement serveur (rembg local) ; renvoie { file } ou { error }. Rien n'est stocké côté serveur. */
export async function enhancePhoto(file) {
  const fd = new FormData();
  fd.append("file", file);
  try {
    const r = await api.post("/crm/photo/enhance", fd, { responseType: "blob", timeout: 30000 });
    const name = (file.name || "photo").replace(/\.[^.]+$/, "") + "_traitee.jpg";
    return { file: new File([r.data], name, { type: "image/jpeg" }), seconds: Number(r.headers?.["x-enhance-seconds"]) || null };
  } catch (e) {
    let msg = "Traitement impossible : la photo d'origine sera utilisée";
    try { const d = e?.response?.data; if (d instanceof Blob) { const j = JSON.parse(await d.text()); if (j?.detail) msg = j.detail; } else if (d?.detail) msg = d.detail; } catch { /* message par défaut */ }
    return { error: msg };
  }
}

const Img = ({ file, label, testId }) => {
  const [src] = useState(() => (file ? URL.createObjectURL(file) : null));
  return (
    <div className="flex flex-col items-center" style={{ gap: 6 }}>
      <div className="flex items-center justify-center overflow-hidden" style={{ width: 200, height: 286, borderRadius: 8, border: "1px solid #26262C", background: "#101013" }} data-testid={testId}>
        {src ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <Loader2 size={18} className="animate-spin" style={{ color: "#8A8A93" }} />}
      </div>
      <span style={{ fontSize: 10, color: "#8A8A93" }}>{label}</span>
    </div>
  );
};

/** 42.4 / 42.6 — fenêtre « Photo du client » : Original | Traitée ; ✕ / Échap = annuler (aucune photo). */
export function PhotoReviewDialog({ review, onKeepOriginal, onUseProcessed, onCancel, testId = "photo-review" }) {
  if (!review) return null;
  const failed = !review.processed;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent onClick={(e) => e.stopPropagation()} style={{ background: "#1C1C1F", border: "1px solid #2E2E33", borderRadius: 14, maxWidth: 520, width: "96vw", padding: 18 }} data-testid={testId}>
        <DialogTitle style={{ fontSize: 14, fontWeight: 700, color: "#F0F0F4" }}>Photo du client</DialogTitle>
        <div className="flex justify-center" style={{ gap: 18, marginTop: 10 }}>
          <Img file={review.original} label="Original" testId={`${testId}-original`} />
          {!failed && <Img file={review.processed} label="Traitée" testId={`${testId}-processed`} />}
        </div>
        {failed && <div style={{ fontSize: 11.5, color: "#FCD34D", textAlign: "center", marginTop: 8 }} data-testid={`${testId}-error`}>{review.error || "Traitement impossible : la photo d'origine sera utilisée"}</div>}
        <div className="flex items-center justify-end" style={{ gap: 8, marginTop: 14 }}>
          <button type="button" onClick={onKeepOriginal} style={{ fontSize: 12, padding: "7px 12px", borderRadius: 8, border: "1px solid #33333B", color: "#E8E8EA", background: "transparent" }} data-testid={`${testId}-keep`}>Garder l'original</button>
          {!failed && <button type="button" onClick={onUseProcessed} style={{ fontSize: 12, fontWeight: 600, padding: "7px 12px", borderRadius: 8, background: "#16A34A", color: "#fff", border: "1px solid #16A34A" }} data-testid={`${testId}-use`}>Utiliser la version traitée</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** 42.3 — `take(file)` : spinner non bloquant pendant le traitement, puis fenêtre de choix ; `onFile(fichierChoisi)` n'est appelé qu'une fois le choix fait. */
export function usePhotoReview(onFile, testId = "photo-review") {
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const take = async (file) => {
    setBusy(true);
    const r = await enhancePhoto(file);
    setBusy(false);
    setReview({ original: file, processed: r.file || null, error: r.error || null });
  };
  const close = () => setReview(null);
  const dialog = <PhotoReviewDialog review={review} testId={testId} onCancel={close} onKeepOriginal={() => { onFile(review.original); close(); }} onUseProcessed={() => { onFile(review.processed); close(); }} />;
  return { busy, take, dialog };
}

export const PhotoBusy = ({ testId }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ gap: 6, background: "rgba(16,16,19,.85)", zIndex: 2 }} data-testid={testId}>
    <Loader2 size={20} className="animate-spin" style={{ color: "#4ADE80" }} />
    <span style={{ fontSize: 10.5, color: "#C9C9CF" }}>Traitement de la photo…</span>
  </div>
);
